import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { afterAuthPath } from "../after-auth.ts";
import { analytics } from "../analytics.ts";
import { api, ApiError } from "../api.ts";
import { arrivalSource } from "../attribution.ts";
import { usePageMeta } from "../components/Layout.tsx";
import { useSession } from "../session.tsx";

// The API enforces this too; mirroring it here turns a 400 into a hint before
// the round trip rather than after it.
const MIN_PASSWORD = 8;

export function Signup() {
  usePageMeta("Create account", "Create a free Coding account. Same account as gazar.dev.");
  const { signedIn } = useSession();
  const navigate = useNavigate();
  // Same as Login: honour wherever a guard bounced them from.
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.signup({
        email,
        password,
        name: name.trim() || undefined,
        marketingConsent,
      });
      signedIn(user);
      // The one signup this app has ever taken from a stranger is unattributable,
      // because nothing recorded which of the four inbound links produced it.
      // The account row cannot carry a source without a change in the shared
      // backend, so the event carries it instead.
      analytics.capture("account_created", {
        src: arrivalSource(),
        marketingConsent,
      });
      navigate(afterAuthPath(from));
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 409
          ? "That email already has an account. Sign in instead."
          : cause instanceof ApiError && cause.status === 400
            ? "Check the form: the password needs at least 8 characters."
            : cause instanceof ApiError && cause.status === 429
              ? "Too many attempts. Wait a few minutes and try again."
              : "Could not reach the server. Try again in a moment.",
      );
      setBusy(false);
    }
  };

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD;

  return (
    <div className="form-page">
      <form className="panel form" onSubmit={submit}>
        <h1>Create an account</h1>
        <p className="muted">
          Free, and it is the same account as gazar.dev. Nothing is charged, now or later.
        </p>
        <label>
          <span>Email</span>
          <input
            type="email"
            value={email}
            autoComplete="username"
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          <span>Name (optional)</span>
          <input
            type="text"
            value={name}
            autoComplete="name"
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="hint-text">
            Never shown publicly. The scoreboard uses a separate handle you choose later.
          </span>
        </label>
        <label>
          <span>Password</span>
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD}
            onChange={(e) => setPassword(e.target.value)}
          />
          {passwordTooShort && <span className="hint-text warn">At least {MIN_PASSWORD} characters.</span>}
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={acceptedTerms}
            required
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
          <span>
            I accept the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
          </span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
          />
          <span>Email me occasionally about new problems. Optional, and off by default.</span>
        </label>

        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy || !acceptedTerms}>
          {busy ? "Creating…" : "Create account"}
        </button>
        <p className="muted small">
          Already have one? <Link to="/login">Sign in</Link>.
        </p>
      </form>
    </div>
  );
}
